import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { Button } from "@/components/ui/button.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { useToast } from "@/components/ui/toast.js";
import { ApiError } from "@/lib/api.js";
import { addDays, formatDate, isoDate } from "@/lib/format.js";
import type { Appointment } from "@/lib/types.js";
import { BookingDialog } from "@/views/booking-dialog.js";
import { WalkinDialog } from "@/views/walkin-dialog.js";
import { AppointmentDialog } from "@/views/appointment-dialog.js";
import { CalendarDayGrid } from "@/views/calendar/calendar-day-grid.js";
import { CalendarWeekGrid } from "@/views/calendar/calendar-week-grid.js";
import { CalendarMonthGrid } from "@/views/calendar/calendar-month-grid.js";
import { CalendarResourceGrid } from "@/views/calendar/calendar-resource-grid.js";
import { CalendarToolbar } from "@/views/calendar/calendar-toolbar.js";
import { CalendarFab } from "@/views/calendar/calendar-fab.js";
import { BlockTimeDialog } from "@/views/calendar/block-time-dialog.js";
import { applyFilters } from "@/views/calendar/calendar-filters.js";
import { useCalendarUrl } from "@/views/calendar/use-calendar-url.js";

/**
 * Calendar grid view per REQ-033 (Fresha pattern) extended by PRD
 * calendar-ux-parity (REQ-CUX-001..015 + NFR-CUX-001..004).
 *
 * Composition root: holds the data fetches (resources, services, customers,
 * appointments, sync conflicts, closures) and dispatches to one of four
 * view-mode grids based on `?view=`. The toolbar drives view + filters; URL
 * sync lives in `use-calendar-url`.
 */
export function CalendarPage(): React.JSX.Element {
  const { api, me } = useTenant();
  const tenantTz = me?.tenant.primary_tz ?? "UTC";
  const { toast } = useToast();

  const url = useCalendarUrl();
  const day = useMemo(() => parseDate(url.state.date), [url.state.date]);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [blockTimeOpen, setBlockTimeOpen] = useState(false);
  const [defaultStart, setDefaultStart] = useState<string | undefined>();
  const [defaultResource, setDefaultResource] = useState<string | undefined>();
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // The data window varies per view mode. The bot/staff API returns the full
  // window; client-side filters apply on top (REQ-CUX-009 §"Search & filter").
  // Bounds are tenant-tz-aware so a tenant in UTC+10 viewed from a UTC+0
  // browser still gets the right calendar-day window — without this padding,
  // appointments at the tenant-day edge are silently missed.
  const dataBounds = useMemo(
    () => boundsForView(url.state.view, day, tenantTz),
    [url.state.view, day, tenantTz]
  );

  useKeyboardShortcuts({
    c: () => {
      setDefaultStart(undefined);
      setDefaultResource(undefined);
      setBookingOpen(true);
    },
    j: () => url.setDate(isoDate(new Date(addDays(day.toISOString(), 1)))),
    k: () => url.setDate(isoDate(new Date(addDays(day.toISOString(), -1))))
  });

  const resources = useAsync(() => api.listResources(), [api]);
  const services = useAsync(() => api.listServices(), [api]);
  const customers = useAsync(() => api.listCustomers(), [api]);
  const appointments = useAsync(
    () => api.listAppointments({ from: dataBounds.from, to: dataBounds.to }),
    [api, dataBounds.from, dataBounds.to]
  );
  const conflicts = useAsync(() => api.listSyncConflicts(), [api]);
  const closures = useAsync(
    () => api.listClosures({ from: dataBounds.from, to: closureWindowEnd(dataBounds.to) }),
    [api, dataBounds.from, dataBounds.to]
  );

  const loading = resources.loading || services.loading || appointments.loading;
  const isToday = isoDate(day) === isoDate(new Date());

  function reload(): void {
    appointments.reload();
    conflicts.reload();
  }

  async function reschedule(appointmentId: string, startsAt: string, resourceId: string): Promise<void> {
    try {
      await api.rescheduleAppointment(appointmentId, { starts_at: startsAt, primary_resource_id: resourceId });
      toast({ title: "Rescheduled", variant: "success" });
      reload();
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "error";
      const message = error instanceof Error ? error.message : String(error);
      toast({ title: code, description: message, variant: "destructive" });
    }
  }

  /**
   * Resize commit — PRD calendar-ux-parity REQ-CUX-006/008. The wire body
   * carries an `idempotency_key` so a retry of the same resize doesn't
   * double-commit. On any error (especially 409 slot_taken), surface a
   * destructive toast with a human-readable message and reload so the
   * optimistic state snaps back.
   */
  async function resize(appointmentId: string, newEndsAt: string): Promise<void> {
    const idempotencyKey = `${resizeJti()}:resize:${appointmentId}:${Date.now()}`;
    try {
      await api.patchAppointment(appointmentId, { ends_at: newEndsAt, idempotency_key: idempotencyKey });
      toast({ title: "Resized", variant: "success" });
      reload();
    } catch (error) {
      if (error instanceof ApiError && error.code === "slot_taken") {
        toast({
          title: "Slot taken",
          description: "Could not resize — slot taken",
          variant: "destructive"
        });
      } else if (error instanceof ApiError && error.code === "service_rules_changed") {
        toast({
          title: "Service rules changed",
          description: "Service rules changed — please re-search.",
          variant: "destructive"
        });
      } else {
        const code = error instanceof ApiError ? error.code : "error";
        const message = error instanceof Error ? error.message : String(error);
        toast({ title: code, description: message, variant: "destructive" });
      }
      reload();
    }
  }

  // Toolbar's selected resource for week/resource view falls back to the
  // first non-deleted resource if the URL doesn't pin one.
  const visibleResources = (resources.data?.data ?? []).filter((r) => !r.deleted_at);
  const selectedResourceId = url.state.resourceId ?? visibleResources[0]?.id ?? "";
  const selectedResource = visibleResources.find((r) => r.id === selectedResourceId) ?? visibleResources[0];

  const customersById = useMemo(
    () => new Map((customers.data?.data ?? []).map((c) => [c.id, c])),
    [customers.data]
  );
  const resourcesById = useMemo(
    () => new Map((resources.data?.data ?? []).map((r) => [r.id, r])),
    [resources.data]
  );

  const filteredAppointments = useMemo(
    () =>
      applyFilters(
        appointments.data?.data ?? [],
        {
          customer: url.state.customer,
          services: url.state.services,
          resources: url.state.resources,
          states: url.state.states,
          search: url.state.search
        },
        customersById,
        resourcesById
      ),
    [
      appointments.data,
      url.state.customer,
      url.state.services,
      url.state.resources,
      url.state.states,
      url.state.search,
      customersById,
      resourcesById
    ]
  );

  const visibleAppointments = filteredAppointments;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Previous"
            onClick={() => url.setDate(isoDate(navTo(url.state.view, day, -1)))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant={isToday ? "soft" : "outline"}
            size="sm"
            onClick={() => url.setDate(undefined)}
            className="mx-1"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Next"
            onClick={() => url.setDate(isoDate(navTo(url.state.view, day, 1)))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <div className="ml-3 flex flex-col">
            <span className="text-sm font-semibold tracking-tight text-foreground">
              {formatDate(day.toISOString())}
            </span>
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {visibleAppointments.length} appointment{visibleAppointments.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        {/* On mobile the FAB (bottom-right) is the primary create surface; hide
            these to reduce visual weight in the header. */}
        <div className="hidden items-center gap-2 lg:flex">
          <Button
            variant="outline"
            onClick={() => {
              setDefaultStart(undefined);
              setDefaultResource(undefined);
              setWalkinOpen(true);
            }}
          >
            <UserPlus className="h-4 w-4" />
            Walk-in
          </Button>
          <Button
            onClick={() => {
              setDefaultStart(undefined);
              setDefaultResource(undefined);
              setBookingOpen(true);
            }}
          >
            <CalendarPlus className="h-4 w-4" />
            New appointment
          </Button>
        </div>
      </div>

      <CalendarToolbar
        view={url.state.view}
        onViewChange={(v) => url.setView(v)}
        selectedResourceId={selectedResourceId}
        onSelectedResourceIdChange={url.setResourceId}
        resources={visibleResources}
        services={services.data?.data ?? []}
        customers={customers.data?.data ?? []}
        filters={{
          customer: url.state.customer,
          services: url.state.services,
          resources: url.state.resources,
          states: url.state.states,
          search: url.state.search
        }}
        onCustomerChange={url.setCustomer}
        onServicesChange={url.setServices}
        onResourcesChange={url.setResourcesFilter}
        onStatesChange={url.setStates}
        onSearchChange={url.setSearch}
        onClearFilters={url.clearFilters}
      />

      {loading ? (
        <Skeleton className="h-[600px] w-full" />
      ) : url.state.view === "day" ? (
        <CalendarDayGrid
          day={day}
          tenantTz={tenantTz}
          resources={resources.data?.data ?? []}
          appointments={visibleAppointments}
          services={services.data?.data ?? []}
          customers={customers.data?.data ?? []}
          conflicts={conflicts.data?.data ?? []}
          closures={closures.data?.data ?? []}
          onReschedule={reschedule}
          onResize={resize}
          onClickEmpty={(startsAt, resourceId) => {
            setDefaultStart(startsAt);
            setDefaultResource(resourceId);
            setBookingOpen(true);
          }}
          onClickAppointment={(a) => setSelectedAppointment(a)}
        />
      ) : url.state.view === "week" ? (
        selectedResource ? (
          <CalendarWeekGrid
            day={day}
            tenantTz={tenantTz}
            resource={selectedResource}
            appointments={visibleAppointments}
            services={services.data?.data ?? []}
            customers={customers.data?.data ?? []}
            conflicts={conflicts.data?.data ?? []}
            closures={closures.data?.data ?? []}
            onResize={resize}
            onClickEmpty={(startsAt, resourceId) => {
              setDefaultStart(startsAt);
              setDefaultResource(resourceId);
              setBookingOpen(true);
            }}
            onClickAppointment={(a) => setSelectedAppointment(a)}
          />
        ) : (
          <EmptyState message="No resources available." />
        )
      ) : url.state.view === "month" ? (
        <CalendarMonthGrid
          day={day}
          tenantTz={tenantTz}
          appointments={visibleAppointments}
          services={services.data?.data ?? []}
          customers={customers.data?.data ?? []}
          closures={closures.data?.data ?? []}
          onClickDay={(dateIso) => url.setView("day", { date: dateIso })}
        />
      ) : selectedResource ? (
        <CalendarResourceGrid
          day={day}
          tenantTz={tenantTz}
          resource={selectedResource}
          appointments={visibleAppointments}
          services={services.data?.data ?? []}
          customers={customers.data?.data ?? []}
          conflicts={conflicts.data?.data ?? []}
          closures={closures.data?.data ?? []}
          onResize={resize}
          onClickEmpty={(startsAt, resourceId) => {
            setDefaultStart(startsAt);
            setDefaultResource(resourceId);
            setBookingOpen(true);
          }}
          onClickAppointment={(a) => setSelectedAppointment(a)}
        />
      ) : (
        <EmptyState message="No resources available." />
      )}

      <AppointmentDialog
        open={selectedAppointment !== null}
        onOpenChange={(v) => !v && setSelectedAppointment(null)}
        appointment={selectedAppointment}
        customers={customers.data?.data ?? []}
        services={services.data?.data ?? []}
        resources={resources.data?.data ?? []}
        conflicts={conflicts.data?.data ?? []}
        onChanged={reload}
      />

      <BookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        customers={customers.data?.data ?? []}
        services={services.data?.data ?? []}
        resources={resources.data?.data ?? []}
        defaultStartsAt={defaultStart}
        defaultResourceId={defaultResource}
        onCreated={reload}
      />
      <WalkinDialog
        open={walkinOpen}
        onOpenChange={(v) => {
          setWalkinOpen(v);
          if (!v) reload();
        }}
        customers={customers.data?.data ?? []}
        services={services.data?.data ?? []}
        resources={resources.data?.data ?? []}
      />
      <BlockTimeDialog
        open={blockTimeOpen}
        onOpenChange={setBlockTimeOpen}
        defaultDate={isoDate(day)}
        onCreated={reload}
      />
      <CalendarFab
        onNewAppointment={() => {
          setDefaultStart(undefined);
          setDefaultResource(undefined);
          setBookingOpen(true);
        }}
        onWalkin={() => {
          setDefaultStart(undefined);
          setDefaultResource(undefined);
          setWalkinOpen(true);
        }}
        onBlockTime={() => setBlockTimeOpen(true)}
      />
    </div>
  );
}

function EmptyState({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="flex h-72 items-center justify-center rounded-lg border bg-card text-sm text-muted-foreground">
      {message}
    </div>
  );
}

/**
 * Convert ?date=YYYY-MM-DD (tenant-local) to a JS Date anchored at noon to
 * avoid DST-edge issues. Falls back to "today" when missing/invalid.
 */
function parseDate(date: string | undefined): Date {
  if (!date) return new Date();
  // Accept exactly YYYY-MM-DD; anything else falls back to today.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return new Date();
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return new Date();
  return d;
}

/**
 * Window for the API fetch given the active view. Day/Resource = 1 day,
 * Week = 7 days starting at the visible day's Monday, Month = the visible
 * 35–42 day calendar grid (PRD §"Success Criteria #4"). For simplicity
 * Month uses 42 days starting at the Monday of the focal month's first day.
 *
 * All bounds are computed in `tenantTz`, not browser-local: the focal `day`
 * is interpreted as a calendar-date label and the bounds are tenant-local
 * 00:00..23:59:59.999 of that range. Without this, a UTC+0 browser viewing
 * a UTC+10 tenant would silently drop appointments at the tenant-day edges.
 */
function boundsForView(view: string, day: Date, tenantTz: string): { from: string; to: string } {
  if (view === "day" || view === "resource") {
    return tenantBounds(isoDate(day), 1, tenantTz);
  }
  if (view === "week") {
    return tenantBounds(isoDate(startOfMondayWeek(day)), 7, tenantTz);
  }
  // month
  const first = new Date(day.getFullYear(), day.getMonth(), 1);
  return tenantBounds(isoDate(startOfMondayWeek(first)), 42, tenantTz);
}

/**
 * Compute the [from, to] ISO bounds for a `days`-long window starting at
 * tenant-local 00:00 on `startLabel` (YYYY-MM-DD). `to` is the ms before
 * tenant-local 00:00 of the day after the window.
 */
function tenantBounds(startLabel: string, days: number, tenantTz: string): { from: string; to: string } {
  const fromMs = tenantMidnightUtcMs(startLabel, tenantTz);
  const toMs = tenantMidnightUtcMs(addDaysLabel(startLabel, days), tenantTz) - 1;
  return { from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() };
}

/**
 * UTC instant of 00:00 in `tenantTz` on the given calendar date label.
 * Anchors at noon UTC of the same date so we sit far from any DST seam,
 * then reads back the wall clock in tenantTz to derive the exact offset.
 */
function tenantMidnightUtcMs(dateLabel: string, tenantTz: string): number {
  const [y, m, d] = dateLabel.split("-").map(Number);
  const candidate = Date.UTC(y, m - 1, d, 12);
  const partsArr = new Intl.DateTimeFormat("en-CA", {
    timeZone: tenantTz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).formatToParts(new Date(candidate));
  const parts: Record<string, string> = {};
  for (const p of partsArr) parts[p.type] = p.value;
  const hour = parts.hour === "24" ? 0 : Number(parts.hour);
  const wallMs = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  // candidate is the UTC instant; in tenantTz its wall clock reads `wallMs`
  // (when interpreted as UTC). The tenant-midnight UTC instant for dateLabel
  // is therefore `candidate - (wallMs - desiredWallMs)`.
  const desiredWallMs = Date.UTC(y, m - 1, d, 0, 0, 0);
  return candidate - (wallMs - desiredWallMs);
}

function addDaysLabel(label: string, days: number): string {
  const [y, m, d] = label.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
}

function startOfMondayWeek(d: Date): Date {
  const out = new Date(d);
  const day = out.getDay();
  const diff = (day + 6) % 7; // Monday-start
  out.setDate(out.getDate() - diff);
  out.setHours(0, 0, 0, 0);
  return out;
}

/**
 * Per-view nav for the < / > buttons. Day/Resource = ±1 day, Week = ±7 days,
 * Month = ±1 calendar month (clamps overflow — Jan 31 → Feb 28 not Mar 2).
 */
function navTo(view: string, day: Date, sign: 1 | -1): Date {
  if (view === "week") {
    const out = new Date(day);
    out.setDate(out.getDate() + 7 * sign);
    return out;
  }
  if (view === "month") {
    return addMonthsToDate(day, sign);
  }
  const out = new Date(day);
  out.setDate(out.getDate() + sign);
  return out;
}

function addMonthsToDate(date: Date, months: number): Date {
  const out = new Date(date);
  const targetMonth = out.getMonth() + months;
  // Snap to the 1st before changing the month so JS's setMonth doesn't
  // overflow forward (e.g. Jan 31 → Mar 3 instead of Feb 28).
  out.setDate(1);
  out.setMonth(targetMonth);
  // Clamp the original day to the target month's length.
  const lastDay = new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate();
  out.setDate(Math.min(date.getDate(), lastDay));
  return out;
}

/** Pull a few days past the visible window so j/k navigation doesn't trigger a fetch storm. */
function closureWindowEnd(toIso: string): string {
  const to = new Date(toIso);
  to.setDate(to.getDate() + 7);
  return to.toISOString();
}

/** UUID surrogate for the JTI portion of `idempotency_key`. */
function resizeJti(): string {
  return globalThis.crypto?.randomUUID?.() ?? `jti-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
